import styles from './segmentedControl.module.css';

export interface SegmentOption<T extends string> {
  readonly value: T;
  readonly label: string;
  /** Shown as a small pill beside the label when present. */
  readonly count?: number;
  /** Renders the count in bold when this segment is the active one - used for the "Por hacer"
   * intent, whose count is the number that most needs the owner's attention right now. */
  readonly emphasizeCount?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  readonly options: readonly SegmentOption<T>[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  readonly ariaLabel: string;
}

/**
 * A fixed, non-scrolling row of 2-3 segments, each an equal-width button with an optional
 * count. Replaces a horizontally-scrolling row of many status chips (mockup principle: "the
 * counts become actionable phrases" - the segments group by intent, not by raw status).
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <div className={styles.segmented} role="group" aria-label={ariaLabel}>
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            className={[styles.segment, active ? styles.segmentActive : null]
              .filter(Boolean)
              .join(' ')}
            aria-pressed={active}
            onClick={() => {
              onChange(option.value);
            }}
          >
            <span>{option.label}</span>
            {option.count !== undefined ? (
              <span
                className={[styles.segmentCount, option.emphasizeCount && active ? styles.segmentCountBold : null]
                  .filter(Boolean)
                  .join(' ')}
              >
                {option.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
